import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, CheckCircle2, Clock, Zap, PlayCircle, RotateCcw, Flame, Award, ArrowRight, BookOpen } from "lucide-react";
import api from "@/lib/api";

const WEEKS = [
  { label: "Week 1", title: "Platform Fundamentals — Admin, CMDB & Security", days: [1, 6] },
  { label: "Week 2", title: "Developer — Scripting, Flow Designer & Integration", days: [7, 16] },
  { label: "Week 3", title: "ITSM — Incident, Problem, Change & Catalog", days: [17, 22] },
  { label: "Week 4", title: "AI & Automation, Certification & Capstone", days: [23, 30] },
];

const DIFFICULTY_COLOR = {
  Beginner: "text-emerald-400 bg-emerald-400/10",
  Intermediate: "text-amber-400 bg-amber-400/10",
  Advanced: "text-orange-400 bg-orange-400/10",
};

function ServiceNowDayCard({ c, idx, isCurrent }) {
  const isLocked = c.status === "locked";
  const isCompleted = c.status === "completed";
  const isInProgress = c.status === "unlocked" && c.attempts > 0;
  const CardWrapper = isLocked ? "div" : Link;
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: Math.min(idx * 0.05, 0.4) }}>
      <CardWrapper
        to={isLocked ? undefined : `/servicenow/challenge/${c.day}`}
        data-testid={`servicenow-roadmap-card-day-${c.day}`}
        className={`relative rounded-xl border p-5 transition-all duration-300 block h-full ${
          isLocked ? "border-white/5 bg-white/[0.01] opacity-50 cursor-not-allowed"
          : isCompleted ? "border-emerald-400/30 bg-emerald-400/[0.03] hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(16,185,129,0.15)]"
          : isInProgress ? "border-emerald-400/30 bg-emerald-400/[0.03] hover:-translate-y-1"
          : "border-emerald-400/30 bg-white/[0.02] hover:border-emerald-400/60 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(34,197,94,0.15)]"
        } ${isCurrent ? "ring-2 ring-emerald-400 animate-pulse" : ""}`}
      >
        {isCurrent && (
          <span className="absolute -top-2.5 left-4 text-[9px] font-bold uppercase tracking-wider bg-emerald-500 text-white px-2 py-0.5 rounded-full" data-testid={`servicenow-current-badge-${c.day}`}>
            Continue Here
          </span>
        )}
        <div className="flex items-center justify-between mb-3">
          <span className="font-heading text-xs font-bold text-slate-400">DAY {c.day}</span>
          {isLocked ? <Lock className="w-4 h-4 text-slate-400" />
            : isCompleted ? <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            : isInProgress ? <RotateCcw className="w-4 h-4 text-emerald-400" />
            : <PlayCircle className="w-4 h-4 text-emerald-400" />}
        </div>
        <h3 className={`font-medium text-sm mb-3 ${isLocked ? "text-slate-400" : "text-white"}`}>{c.title}</h3>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${DIFFICULTY_COLOR[c.difficulty] || "text-slate-400 bg-white/5"}`}>{c.difficulty}</span>
          <span className="text-[10px] text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" /> {c.estimated_minutes}m</span>
          <span className="text-[10px] text-[#4ade80] flex items-center gap-1"><Zap className="w-3 h-3" /> {c.xp_reward} XP</span>
        </div>
        {isCompleted && c.quiz_score != null && <div className="mt-3 text-xs text-emerald-400 font-semibold">Score: {c.quiz_score}%</div>}
        {isInProgress && <div className="mt-3 text-xs text-emerald-400 font-semibold">In Progress · {c.attempts} attempt{c.attempts > 1 ? "s" : ""}</div>}
      </CardWrapper>
    </motion.div>
  );
}

export default function ServiceNowRoadmapPage() {
  const [challenges, setChallenges] = useState([]);
  const [trackStats, setTrackStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/servicenow/challenges"), api.get("/servicenow/track/me")])
      .then(([cRes, tRes]) => { setChallenges(cRes.data); setTrackStats(tRes.data); })
      .finally(() => setLoading(false));
  }, []);

  const currentDay = challenges.find((c) => c.status === "unlocked")?.day;
  const completedCount = challenges.filter((c) => c.status === "completed").length;
  const totalDays = challenges.length || trackStats?.total_days || 30;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="servicenow-roadmap-loading">
        <div className="w-10 h-10 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="servicenow-roadmap-page">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400">ServiceNow Track</span>
          <h1 className="font-heading text-3xl font-semibold text-white mt-2">30-Day ServiceNow Challenge</h1>
          <p className="text-slate-400 text-sm mt-1">Platform Fundamentals, CMDB & Security — then Development, Flow Designer, ITSM, AI & Automation and Certification Prep.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-3 py-1.5" data-testid="servicenow-streak-badge">
            <Flame className="w-3.5 h-3.5 text-emerald-400" />
            <span className="text-xs font-bold text-emerald-300">{trackStats?.streak_current ?? 0} day streak</span>
          </div>
          <Link to="/servicenow-curriculum" data-testid="servicenow-curriculum-link" className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full px-3 py-1.5 text-xs font-bold text-white transition-colors">
            <BookOpen className="w-3.5 h-3.5 text-emerald-400" /> Full Curriculum
          </Link>
          <Link to="/servicenow/certificate" data-testid="servicenow-certificate-link" className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full px-3 py-1.5 text-xs font-bold text-white transition-colors">
            <Award className="w-3.5 h-3.5 text-[#4ade80]" /> Certificate <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>

      <div className="glass-panel rounded-2xl p-5 mb-8" data-testid="servicenow-progress-bar-panel">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-slate-300">Track Progress</span>
          <span className="text-xs font-bold text-emerald-400">{completedCount}/{totalDays} days complete</span>
        </div>
        <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-emerald-500 to-green-400 rounded-full transition-all duration-500" style={{ width: `${(completedCount / totalDays) * 100}%` }} />
        </div>
      </div>

      <div className="space-y-8">
        {WEEKS.map((week) => {
          const weekChallenges = challenges.filter((c) => c.day >= week.days[0] && c.day <= week.days[1]);
          if (weekChallenges.length === 0) return null;
          const weekCompleted = weekChallenges.filter((c) => c.status === "completed").length;
          return (
            <div key={week.label} data-testid={`servicenow-roadmap-week-${week.label.replace(" ", "-").toLowerCase()}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-400">{week.label}</span>
                  <span className="text-sm font-semibold text-white">{week.title}</span>
                </div>
                <span className="text-[11px] text-slate-400">{weekCompleted}/{weekChallenges.length}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {weekChallenges.map((c, idx) => (
                  <ServiceNowDayCard key={c.day} c={c} idx={idx} isCurrent={c.day === currentDay} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
